import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Users, FileText, BarChart3, LogOut, Settings, Home, BookOpen, Ticket, Wrench, Receipt, PhoneCall } from "lucide-react";
import useDocumentTitle from "../../hooks/useDocumentTitle";

export default function AdminLayout() {
  useDocumentTitle("Panel de administración");
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Perms: gestion users only see Discount Codes if their SA granted the
  // capability. Superadmin + admin roles always see everything.
  const isManagerBypass = user?.role === "superadmin" || user?.role === "admin";
  const perms = user?.perms || {};
  const canDiscountCodes = isManagerBypass || !!perms.create_discount_codes;
  const canContactRequests = isManagerBypass || !!perms.view_contact_requests;

  // Content pages (top of the sidebar).
  const contentItems = [
    { to: "/admin", label: "Progreso", icon: BarChart3, end: true },
    { to: "/admin/users", label: "Personas usuarias", icon: Users },
    { to: "/admin/courses", label: "Cursos", icon: FileText },
    { to: "/admin/workshops", label: "Talleres", icon: Wrench },
    ...(isManagerBypass ? [{ to: "/admin/invoices", label: "Facturas", icon: Receipt }] : []),
    ...(canDiscountCodes ? [{ to: "/admin/discount-codes", label: "Códigos", icon: Ticket }] : []),
    ...(canContactRequests ? [{ to: "/admin/contact-requests", label: "Te llamamos", icon: PhoneCall }] : []),
    { to: "/admin/settings", label: "Ajustes", icon: Settings },
  ];

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
      isActive
        ? "bg-brand-soft text-brand-ink font-medium"
        : "text-gray-600 hover:bg-gray-100 hover:text-brand-ink"
    }`;

  return (
    <div className="min-h-screen flex bg-[#F7FAF9]">
      <aside
        className="w-64 bg-white border-r border-gray-200 flex flex-col sticky top-0 h-screen self-start"
        data-testid="admin-sidebar"
      >
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="laclaseencasa" className="w-9 h-9 object-contain" />
            <div>
              <span className="font-display text-base text-brand-ink leading-none block">laclaseencasa</span>
              <span
                className="text-[10px] uppercase tracking-wider text-gray-500 font-mono-data"
                data-testid="admin-role-badge"
              >
                {user?.role || "gestion"}
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3 truncate">{user?.email}</p>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {contentItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase()}`}
              className={navLinkClass}
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}

          {/* Navigation / account actions — sit right below Ajustes, separated
              by a thin rule so they're visually distinct from content pages. */}
          <div className="pt-2 mt-2 border-t border-gray-200 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100 hover:text-brand-ink transition-colors w-full"
              data-testid="admin-back-to-home"
            >
              <Home className="w-4 h-4" />
              Volver a la home
            </Link>
            <Button
              variant="ghost"
              className="w-full justify-start text-gray-600 hover:text-brand-ink px-3 py-2 h-auto text-sm font-normal rounded-lg"
              onClick={() => {
                logout();
                navigate("/login");
              }}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Cerrar sesión
            </Button>
          </div>

          {/* Help / manual — pushed to the bottom of the nav column with
              another separator so it sits apart from the main groups. */}
          <div className="pt-2 mt-2 border-t border-gray-200">
            <NavLink
              to="/admin/help"
              data-testid="nav-manual"
              className={navLinkClass}
            >
              <BookOpen className="w-4 h-4" />
              Manual
            </NavLink>
          </div>
        </nav>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
